import { useEffect, useState, useCallback } from 'react'
import { useSession } from '@/context/SessionContext'
import { savePushSubscription } from '@/lib/queries/notifications'
import { captureException } from '@/lib/sentry'

const VAPID_PUBLIC_KEY =
  'BKyUeJTGz08ZxyX4k41kRC3zYuAVI_QzTu-bZQLAXnwVkyoRj7-l3TEdqHeuwvLAUg_8NAcYCf3-pT1GPDusZMA'

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)))
}

const isSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window

export function usePushNotifications() {
  const { user } = useSession()
  const [permission, setPermission] = useState(() =>
    isSupported() ? Notification.permission : 'unsupported'
  )
  const [swReady, setSwReady] = useState(false)

  // The service worker itself is registered at app entry (main.jsx). Here we
  // just wait for it to be ready so we can use the registration for push
  // subscriptions.
  useEffect(() => {
    if (!isSupported()) return
    navigator.serviceWorker.ready
      .then(() => setSwReady(true))
      .catch(err => {
        captureException(err, { tags: { feature: 'push-sw-ready' } })
      })
  }, [])

  const subscribe = useCallback(async () => {
    if (!isSupported() || !swReady || !user) return false
    try {
      const registration = await navigator.serviceWorker.ready
      const existing = await registration.pushManager.getSubscription()
      const sub =
        existing ||
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        }))

      const json = sub.toJSON()
      await savePushSubscription(user.id, {
        endpoint: json.endpoint,
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
      })

      return true
    } catch (err) {
      captureException(err, { tags: { feature: 'push-subscribe' } })
      return false
    }
  }, [user, swReady])

  const requestPermission = useCallback(async () => {
    if (!isSupported()) return 'unsupported'
    const result = await Notification.requestPermission()
    setPermission(result)
    if (result === 'granted') await subscribe()
    return result
  }, [subscribe])

  return { permission, supported: isSupported(), requestPermission }
}
